import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 1500;

const SupportThankYouPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [state, setState] = useState({ phase: 'checking', data: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: 'error', data: null });
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/support/checkout-status/${sessionId}`);
        if (cancelled) return;

        const status = res.data?.status;
        if (status === 'paid') {
          setState({ phase: 'success', data: res.data });
          return;
        }
        if (status === 'expired' || status === 'failed') {
          setState({ phase: 'error', data: res.data });
          return;
        }

        attempts += 1;
        if (attempts >= MAX_POLL_ATTEMPTS) {
          setState({ phase: 'pending', data: res.data });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setState({ phase: 'error', data: null });
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const { phase, data } = state;

  return (
    <div data-testid="support-thankyou-page" className="bg-slate-50 min-h-screen">
      <section className="bg-slate-900 text-white py-16 md:py-24">
        <div className="max-w-3xl mx-auto container-padding text-center">
          {phase === 'checking' && (
            <>
              <Loader2 className="w-10 h-10 text-cyan-400 mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
                Confirming your contribution…
              </h1>
              <p className="text-base text-slate-300">
                This usually takes only a few seconds.
              </p>
            </>
          )}

          {phase === 'success' && (
            <>
              <CheckCircle2 className="w-12 h-12 text-cyan-400 mx-auto mb-6" />
              <p className="caption text-cyan-400 mb-3">Support Confirmed</p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
                Your contribution is recorded.
              </h1>
              <p className="text-lg text-slate-300">
                The standard exists because people fund it. Thank you.
              </p>
            </>
          )}

          {phase === 'pending' && (
            <>
              <Loader2 className="w-10 h-10 text-cyan-400 mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
                Your contribution is being processed.
              </h1>
              <p className="text-base text-slate-300">
                Stripe is finalising the transaction. You will receive an
                email receipt shortly.
              </p>
            </>
          )}

          {phase === 'error' && (
            <>
              <AlertCircle className="w-10 h-10 text-amber-400 mx-auto mb-6" />
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
                We couldn’t confirm your contribution.
              </h1>
              <p className="text-base text-slate-300">
                If you completed payment, you will still receive a receipt
                from Stripe. Otherwise, please try again.
              </p>
            </>
          )}
        </div>
      </section>

      {phase === 'success' && data && (
        <section className="py-16">
          <div className="max-w-2xl mx-auto container-padding">
            <div className="bg-white border border-slate-200 p-8">
              <p className="caption text-slate-500 mb-6">Receipt summary</p>
              <dl className="space-y-4 text-sm">
                {data.name && (
                  <div className="flex justify-between border-b border-slate-100 pb-3">
                    <dt className="text-slate-500">Name</dt>
                    <dd className="text-slate-900 font-medium" data-testid="receipt-name">{data.name}</dd>
                  </div>
                )}
                {data.email && (
                  <div className="flex justify-between border-b border-slate-100 pb-3">
                    <dt className="text-slate-500">Email</dt>
                    <dd className="text-slate-900 font-medium">{data.email}</dd>
                  </div>
                )}
                <div className="flex justify-between border-b border-slate-100 pb-3">
                  <dt className="text-slate-500">Amount</dt>
                  <dd className="text-slate-900 font-medium" data-testid="receipt-amount">
                    ${Number(data.amount_usd || 0).toLocaleString()} USD
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Frequency</dt>
                  <dd className="text-slate-900 font-medium" data-testid="receipt-interval">
                    {data.interval === 'monthly' ? 'Monthly recurring' : 'One-time'}
                  </dd>
                </div>
              </dl>
              <p className="text-xs text-slate-500 mt-6 leading-relaxed">
                A receipt has been emailed to you by Stripe. SAFE is not a
                registered non-profit; this contribution is not tax-deductible.
              </p>
            </div>
          </div>
        </section>
      )}

      <section className="py-12 text-center">
        <div className="max-w-2xl mx-auto container-padding">
          <Link to="/" className="btn-primary" data-testid="thankyou-home-link">
            Return to SAFE Standard
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SupportThankYouPage;
